import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { pixelsToPercentage } from '@/lib/deviceDetection';

export type PlacementState = 'idle' | 'placing';

export interface PlacedSignature {
  id: string;
  type: 'signature' | 'initials';
  data: string;
  page: number;
  x: number; // Pixel coordinate (legacy/fallback)
  y: number; // Pixel coordinate (legacy/fallback)
  width?: number;
  height?: number;
  placedAt: string; // ISO timestamp when signature was placed
  // Percentage-based coordinates for zoom/scale independence
  xPercent?: number; // X position as percentage (0-100)
  yPercent?: number; // Y position as percentage (0-100)
}

interface UseSignaturePlacementOptions {
  onPlaced: (signature: PlacedSignature) => void;
  onCancelled: () => void;
}

export function useSignaturePlacement({ onPlaced, onCancelled }: UseSignaturePlacementOptions) {
  const [state, setState] = useState<PlacementState>('idle');
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const pendingDataRef = useRef<{ data: string; type: 'signature' | 'initials' } | null>(null);

  const startPlacing = useCallback((data: string, type: 'signature' | 'initials') => {
    pendingDataRef.current = { data, type };
    setState('placing');
  }, []);

  const cancelPlacing = useCallback(() => {
    pendingDataRef.current = null;
    setState('idle');
    onCancelled();
  }, [onCancelled]);

  useEffect(() => {
    if (state !== 'placing') return;

    const handlePointerMove = (e: PointerEvent) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    const handlePointerUp = (e: PointerEvent) => {
      const pending = pendingDataRef.current;
      if (!pending) {
        setState('idle');
        return;
      }

      // Find the page container under the pointer
      const element = document.elementFromPoint(e.clientX, e.clientY);
      const pageContainer = element?.closest('.pdf-page-container[data-page-number]') as HTMLElement | null;

      if (!pageContainer) {
        toast.error('Please click on a PDF page to place your signature');
        setState('idle');
        pendingDataRef.current = null;
        onCancelled();
        return;
      }

      // Page number: PDF.js pages are 1-based, and data-page-number stores the 1-based page number
      const pageNumber = parseInt(pageContainer.dataset.pageNumber || '1', 10);
      const rect = pageContainer.getBoundingClientRect();

      // Get the current scale from the container's transform (for mobile scaling)
      const scaleMatch = pageContainer.style.transform?.match(/scale\(([\d.]+)\)/);
      const scale = scaleMatch ? parseFloat(scaleMatch[1]) : 1;

      // Compute coordinates relative to the page container (in unscaled coordinates)
      // The rect is the scaled bounding box, so we divide by scale to get unscaled coords
      const relativeX = (e.clientX - rect.left) / scale;
      const relativeY = (e.clientY - rect.top) / scale;

      // Signature dimensions (unscaled DOM pixels)
      const isInitials = pending.type === 'initials';
      const width = isInitials ? 60 : 150;
      const height = isInitials ? 30 : 50;

      // Page dimensions (unscaled) - these are the actual CSS dimensions of the page container
      const pageWidth = rect.width / scale;
      const pageHeight = rect.height / scale;

      // Clamp within page bounds (center the signature on cursor)
      const halfWidth = width / 2;
      const halfHeight = height / 2;
      const clampedX = Math.max(halfWidth, Math.min(pageWidth - halfWidth, relativeX));
      const clampedY = Math.max(halfHeight, Math.min(pageHeight - halfHeight, relativeY));

      // Final position is top-left corner of the signature
      const finalX = clampedX - halfWidth;
      const finalY = clampedY - halfHeight;

      // Convert to percentage-based coordinates for zoom/scale independence
      const percentCoords = pixelsToPercentage(finalX, finalY, pageWidth, pageHeight);

      console.log('[useSignaturePlacement] Computing placement coordinates', {
        pageNumber,
        clientCoords: { x: e.clientX, y: e.clientY },
        rectTopLeft: { left: rect.left, top: rect.top },
        scale,
        relativeCoords: { x: relativeX, y: relativeY },
        pageDimensions: { width: pageWidth, height: pageHeight },
        signatureDimensions: { width, height },
        finalPosition: { x: finalX, y: finalY },
        percentPosition: { x: percentCoords.x, y: percentCoords.y },
      });

      // Create the signature annotation
      // Coordinates are stored as percentages (primary) with pixel fallback
      const signature: PlacedSignature = {
        id: `sig-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: pending.type,
        data: pending.data,
        page: pageNumber,
        x: finalX, // Pixel fallback for legacy compatibility
        y: finalY, // Pixel fallback for legacy compatibility
        xPercent: percentCoords.x,
        yPercent: percentCoords.y,
        width,
        height,
        placedAt: new Date().toISOString(),
      };

      onPlaced(signature);
      pendingDataRef.current = null;
      setState('idle');
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelPlacing();
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [state, onPlaced, onCancelled, cancelPlacing]);

  return {
    state,
    cursorPosition,
    pendingData: pendingDataRef.current,
    startPlacing,
    cancelPlacing,
  };
}
